<template>
  <section class="module-load" role="status" aria-labelledby="module-load-title">
    <span
      class="module-load__badge"
      :class="kind === 'offline' ? 'module-load__badge--offline' : 'module-load__badge--failed'"
    >
      <v-icon size="22" :color="kind === 'offline' ? 'warning' : 'error'">{{
        kind === "offline" ? "cloud_off" : "error_outline"
      }}</v-icon>
    </span>
    <h1 id="module-load-title" class="module-load__title">{{ title }}</h1>
    <p class="module-load__body">{{ body }}</p>
    <v-btn class="module-load__retry" color="primary" variant="flat" @click="onRetry">{{ t("common.tryAgain") }}</v-btn>
  </section>
</template>

<script lang="ts">
import { computed, defineComponent, type PropType } from "vue";
import { useI18n } from "vue-i18n";
import type { ModuleLoadKind } from "@/spa/moduleLoadFailure";

/**
 * Full-content stand-in for a tenant module whose bundle did not import
 * (INS-9). Rendered inside `<v-main>`, so the top bar and sidebar stay.
 */
export default defineComponent({
  name: "ModuleLoadScreen",
  props: {
    kind: {
      type: String as PropType<ModuleLoadKind>,
      required: true,
    },
  },
  emits: ["retry"],
  setup(props, { emit }) {
    const { t } = useI18n();
    const title = computed(() =>
      props.kind === "offline" ? t("moduleLoad.offlineTitle") : t("moduleLoad.failedTitle"),
    );
    const body = computed(() =>
      props.kind === "offline" ? t("moduleLoad.offlineBody") : t("moduleLoad.failedBody"),
    );
    function onRetry(): void {
      emit("retry");
    }
    return { t, title, body, onRetry };
  },
});
</script>

<style scoped>
.module-load {
  min-height: calc(100dvh - var(--metaship-header-height));
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 32px 24px;
  gap: 8px;
}
.module-load__badge {
  display: grid;
  place-items: center;
  width: 44px;
  height: 44px;
  margin-bottom: 8px;
  border-radius: 50%;
}
.module-load__badge--offline {
  background: rgba(var(--v-theme-warning), 0.14);
}
.module-load__badge--failed {
  background: rgba(var(--v-theme-error), 0.12);
}
.module-load__title {
  max-width: 26rem;
  margin: 0;
  font-size: 20px;
  font-weight: 700;
  letter-spacing: -0.015em;
  color: var(--sh-text);
}
.module-load__body {
  max-width: 26rem;
  margin: 0 0 12px;
  font-size: 13px;
  line-height: 1.5;
  color: var(--sh-text-60);
}
.module-load__retry {
  min-width: 140px;
  font-weight: 600;
}
</style>
